import { Fragment, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { LabelChip } from '@components/Label';
import { CssNoOptionsText } from '@components/Select';
import { Typography } from '@components/Typography';
import { usePopupZIndex } from '@components/Modal/popupLayer';
import { useTranslation } from '@hooks';
import {
    StyledAutocomplete,
    CustomPopper,
    ErrorWrapper,
    AllOption,
    OneGroupOption,
    GroupOption,
    GroupWrapper,
    GroupHeader,
    BaseOptionWrapper,
    CssCheckbox,
    ErrText,
} from './styles';
import { searchOptions } from './searchOptions';

const BaseOptionComponent = ({ id, label, ...props }) => {
    return (
        <BaseOptionWrapper key={id} component="li" {...props}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {label}
            </Typography>
        </BaseOptionWrapper>
    );
};

const Autocomplete = ({
    className,
    label,
    options,
    onChangeAutoComplete,
    ValueComponent,
    OptionComponent,
    optionLabelProp = 'label',
    inputProps = {},
    readOnly,
    inputRef,
    error,
    groupMultiProps = null,
    disabled,
    withCaret = false,
    keepOpenOnSelect = false,
    ...props
}) => {
    const [selectedItems, setSelectedItems] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const [query, setQuery] = useState('');
    const userHighlighted = useRef(false);
    const erasingQuery = useRef(false);

    const { t } = useTranslation();

    useEffect(() => {
        setSelectedItems(props?.value || []);
    }, [props?.value]);

    const extendedOptions = useMemo(
        () => [...(groupMultiProps ? [{ id: 'all', label: t('common.all') }] : []), ...options],
        [options, groupMultiProps],
    );

    const listedOptions = useMemo(() => searchOptions(options, query), [options, query]);
    const listedIds = useMemo(() => new Set(listedOptions.map(option => option.id)), [listedOptions]);

    const onSelectItems = useCallback(
        items => {
            setSelectedItems(items);
            onChangeAutoComplete(items);
        },
        [onChangeAutoComplete],
    );

    const selectedIds = useMemo(() => new Set(selectedItems.map(item => item.id)), [selectedItems]);

    const checkOption = useCallback(option => selectedIds.has(option.id), [selectedIds]);

    const checkOptions = useCallback(
        list => {
            const selectedCount = list.filter(checkOption).length;
            return {
                all: list.length > 0 && selectedCount === list.length,
                some: selectedCount > 0 && selectedCount < list.length,
            };
        },
        [checkOption],
    );

    const withoutOptions = list => {
        const ids = new Set(list.map(option => option.id));
        return selectedItems.filter(item => !ids.has(item.id));
    };

    const withOptions = list => [...selectedItems, ...list.filter(option => !checkOption(option))];

    const allOptionState = useMemo(() => checkOptions(listedOptions), [checkOptions, listedOptions]);

    const selectAll = () => {
        onSelectItems(allOptionState.all ? withoutOptions(listedOptions) : withOptions(listedOptions));
    };

    const listedGroupOptions = useCallback(
        group => listedOptions.filter(option => option.group === group),
        [listedOptions],
    );

    const checkGroup = useCallback(
        group => checkOptions(listedGroupOptions(group)),
        [checkOptions, listedGroupOptions],
    );

    const selectGroup = group => {
        const groupedOptions = listedGroupOptions(group);
        const { all, some } = checkOptions(groupedOptions);
        onSelectItems(all || some ? withoutOptions(groupedOptions) : withOptions(groupedOptions));
    };

    const hasOneGroup = useMemo(
        () => groupMultiProps && new Set(options.map(option => option.group)).size === 1,
        [options],
    );

    const popupZIndex = usePopupZIndex();
    const customPopper = useCallback(
        popperProps => <CustomPopper {...popperProps} popupZIndex={popupZIndex} />,
        [popupZIndex],
    );

    // INFO: partlyAssignedCallback throws error in console regarding to html attribute
    const { partlyAssignedCallback, ...restGroupMultiProps } = groupMultiProps || {}; // eslint-disable-line

    return (
        <ErrorWrapper error={!!error} className={className}>
            <StyledAutocomplete
                {...props}
                multiple
                options={extendedOptions}
                readOnly={readOnly || disabled}
                value={props?.value || selectedItems}
                getOptionLabel={option => option.id}
                disabled={disabled}
                withCaret={withCaret}
                renderInput={params => (
                    <TextField
                        {...params}
                        ref={inputRef}
                        variant="outlined"
                        label={label}
                        required={props.required}
                        slotProps={{
                            ...params.slotProps,
                            htmlInput: {
                                ...params.slotProps.htmlInput,
                                ...(groupMultiProps
                                    ? {}
                                    : {
                                          onFocus: () => setIsFocused(true),
                                          onBlur: () => setIsFocused(false),
                                          ...inputProps,
                                      }),
                            },
                        }}
                        error={!!error}
                    />
                )}
                onChange={(_, items, reason) => {
                    onSelectItems(items);
                    if (!groupMultiProps) setIsFocused(keepOpenOnSelect || reason === 'clear');
                }}
                {...(groupMultiProps
                    ? {
                          disableCloseOnSelect: true,
                          groupBased: !!groupMultiProps,
                          groupBasedSelected: selectedItems.length > 0,
                          groupBy: option => option.group,
                          renderOption: (props, option) => {
                              const { all, some } = allOptionState;
                              return option.id === 'all' ? (
                                  <AllOption {...props} component="li" key={option.id}>
                                      <CssCheckbox checked={all} indeterminate={some} />
                                      <Typography variant="body2" sx={{ lineHeight: '32px !important' }}>
                                          {t('common.all')}
                                      </Typography>
                                  </AllOption>
                              ) : hasOneGroup ? (
                                  <OneGroupOption
                                      {...props}
                                      key={option.id}
                                      component="li"
                                      aria-selected={checkOption(option)}
                                  >
                                      <CssCheckbox checked={checkOption(option)} />
                                      <Typography variant="body2">{option.label}</Typography>
                                  </OneGroupOption>
                              ) : (
                                  <GroupOption
                                      {...props}
                                      key={option.id}
                                      component="li"
                                      aria-selected={checkOption(option)}
                                  >
                                      <CssCheckbox checked={checkOption(option)} />
                                      <Typography variant="body2">{option.label}</Typography>
                                  </GroupOption>
                              );
                          },
                          renderGroup: params => {
                              if (!params.group || hasOneGroup) {
                                  return (
                                      <GroupWrapper
                                          key={params.key}
                                          sx={{
                                              borderBottom: !params.group ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                                              paddingBottom: !params.group ? '8px' : 0,
                                              paddingTop: !params.group ? 0 : '8px',
                                          }}
                                      >
                                          {params.children}
                                      </GroupWrapper>
                                  );
                              }
                              const { all, some } = checkGroup(params.group);
                              return (
                                  <Fragment key={params.key}>
                                      <div
                                          style={{ display: 'flex', alignItems: 'center', gap: '3px', margin: '3px 0' }}
                                      >
                                          <i />
                                          <CssCheckbox
                                              checked={all}
                                              indeterminate={some}
                                              onChange={() => selectGroup(params.group)}
                                          />
                                          <GroupHeader variant="caption">{params.group}</GroupHeader>
                                      </div>
                                      <GroupWrapper>{params.children}</GroupWrapper>
                                  </Fragment>
                              );
                          },
                          renderValue: () => {
                              return selectedItems.map((option, index) => (
                                  <LabelChip
                                      key={index}
                                      type={option.label}
                                      isPartlyAssigned={groupMultiProps?.partlyAssignedCallback?.(option.id)}
                                      label={
                                          <Typography variant="caption" sx={{ display: 'block', height: 14 }}>
                                              {option.label}
                                          </Typography>
                                      }
                                      clickable
                                      deleteIcon={<CloseIcon onMouseDown={e => e.stopPropagation()} />}
                                      onDelete={() => onSelectItems(withoutOptions([option]))}
                                      sx={{ margin: '0 3px 3px 0' }}
                                  />
                              ));
                          },
                          ...restGroupMultiProps,
                          inputValue: query,
                          onInputChange: (_, value, reason) => {
                              if (reason !== 'input' && reason !== 'clear') return;
                              userHighlighted.current = false;
                              setQuery(value);
                          },
                          onClose: () => {
                              userHighlighted.current = false;
                              setQuery('');
                          },
                          onHighlightChange: (_, option, reason) => {
                              userHighlighted.current = !!option && reason !== 'auto';
                          },
                          onKeyDown: event => {
                              if (event.key !== 'Backspace') return;
                              if (!event.repeat) erasingQuery.current = query !== '';
                              else if (erasingQuery.current && !query) event.defaultMuiPrevented = true;
                          },
                          onChange: (event, items, reason, details) => {
                              if (reason === 'clear' && query) return;
                              if (event?.key === 'Enter' && !userHighlighted.current) return;
                              if (details?.option?.id === 'all') selectAll();
                              else onSelectItems(items);
                          },
                          filterOptions: candidates =>
                              candidates.filter(option =>
                                  option.id === 'all' ? listedOptions.length > 0 : listedIds.has(option.id),
                              ),
                      }
                    : {
                          open: isFocused,
                          disableCloseOnSelect: keepOpenOnSelect,
                          renderOption: (optionProps, option) =>
                              OptionComponent ? (
                                  <BaseOptionWrapper {...optionProps} component="li" key={option.id}>
                                      <OptionComponent option={option} />
                                  </BaseOptionWrapper>
                              ) : (
                                  <BaseOptionComponent
                                      {...optionProps}
                                      key={option.id}
                                      id={option.id}
                                      label={option[optionLabelProp]}
                                  />
                              ),
                          renderValue: options => {
                              return options.map(option =>
                                  ValueComponent ? (
                                      <ValueComponent key={option.id} option={option} />
                                  ) : (
                                      <BaseOptionComponent
                                          key={option.id}
                                          id={option.id}
                                          label={option[optionLabelProp]}
                                      />
                                  ),
                              );
                          },
                      })}
                noOptionsText={<CssNoOptionsText>{t('common.noOptions')}</CssNoOptionsText>}
                slots={{ ...props.slots, popper: customPopper }}
                slotProps={{
                    ...props.slotProps,
                    listbox: {
                        role: 'list-box',
                        sx: {
                            overflow: 'auto !important',
                            '& .MuiAutocomplete-option': {
                                minHeight: '32px',
                                flexWrap: 'nowrap',
                                '@media (max-width: 600px)': {
                                    width: 'calc(100% - 16px)',
                                },
                            },
                            '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                            },
                            '& .MuiAutocomplete-option[aria-selected="true"]': {
                                backgroundColor: '#E4EAEB !important',
                            },
                        },
                    },
                }}
                popupIcon={withCaret ? <ArrowDropDownIcon /> : null}
                error={!!error}
            />
            {error && error?.message && <ErrText>{t(error.message)}</ErrText>}
        </ErrorWrapper>
    );
};

export default Autocomplete;
